/* eslint-disable camelcase */

import { AxiosRequestConfig, AxiosResponse } from "axios";
import { SWRConfiguration } from "swr";
import { ApiArrayResponse, ApiResponse, ServerResponse } from "./axiosConfig";
import { useSwr, axiosInst } from "./axiosHVS";
import { HvsEntity } from "./hvsBase";

export type CameraEntity = HvsEntity;

export enum CameraTypesValues {
  Onvif = "onvif",
  RTSP = "ip",
  VMS = "vms",
  KASTLE = "kastle",
  ORCHID = "orchid",
}

export enum CameraLabelValues {
  VendorModel = "Vendor Model",
}

export enum CameraTypes {
  Onvif = "ONVIF",
  RTSP = "RTSP",
  VMS = "VMS",
}

export enum CameraStatuses {
  CameraOnline = "CameraOnline",
  CameraOffline = "CameraOffline",
  CameraNoLongerExist = "CameraNoLongerExist",
  StatusNotAvailable = "StatusNotAvailable",
  GatewayOffline = "GatewayOffline",
}

export enum CameraAttributeValues {
  Onvif = "onvif",
  RTSP = "ip",
}

export enum CameraAttributes {
  IpAddress = "ip_address",
  Port = "port",
  StreamingIp = "av_stream_uri",
  StreamingPort = "av_stream_port",
  StreamingEndPoint = "rtsp_address",
  Secure = "rtsp_secure",
  Microphone = "has_microphone",
  Recording = "recording_enabled",
  CanPlayback = "can_playback",
  Type = "type",
  ICMP = "ICMP",
  Ptz = "is_ptz",
  Vendor = "vendor",
  PositioningAltitude = "positioning_altitude",
  SlewEnabled = "slew_enabled",
  intFpsValue = "fps",
}
export enum PresetAttributes {
  Azimuth = "azimuth",
}
export enum VmsAttributes {
  Vendor = "vendor",
  SlewEnabled = "slew_enabled",
}

export enum CameraVendorValues {
  Hitachi = "hitachi",
}
export const CameraHidePassword = "••••••";
export interface CameraResponse {
  results: CameraSearchEntity[];
  filters: CameraFilter[];
  totalCount: number;
  pageNr: number;
  pageSize: number;
}

export interface CameraSearchEntity {
  id: string;
  name: string;
  description: string;
  status: string;
  typeName: string;
  parentName: string;
  vmsName: string;
  gatewayName: string;
  ptz: boolean;
  audio: boolean;
  location: boolean;
  vmsId: string;
  entityTypeId: string;
  createDate: Date;
  updateDate: Date;
}

export interface CameraFilter {
  name: string;
  values: CameraFilterValue[];
}

export interface CameraFilterValue {
  name: string;
  value: string;
  i18n: boolean;
}

export interface CameraFilters {
  name: string;
  values: string[];
}

const baseCameraSearchKey = "/bulk/cameras/get";

export const useCameras = (
  params: {
    pageNr?: number;
    pageSize?: number;
    orderBy?: string;
    Order?: string;
    SearchKey?: string;
    calculateFilter?: boolean;
  } = {
    pageNr: 1,
    pageSize: 200,
    orderBy: undefined,
    Order: "desc",
    SearchKey: undefined,
    calculateFilter: true,
  },
  config?: SWRConfiguration,
  requestBody?: CameraFilters[]
): ServerResponse<ApiResponse<CameraResponse>> =>
  useSwr<ApiResponse<CameraResponse>>(
    `${baseCameraSearchKey}?calculateFilter=${params.calculateFilter}&pageNr=${
      params.pageNr
    }&pageSize=${params.pageSize}${
      params.orderBy != null
        ? `&SortName=${params.orderBy}&SortOrder=${params.Order}`
        : ""
    }${params.SearchKey != null ? `&Keyword=${params.SearchKey}` : ""}`,
    config,
    requestBody ?? []
  );

export const useCamera = (
  id: string,
  typeId: string,
  config?: SWRConfiguration
): ServerResponse<ApiResponse<CameraEntity>> => {
  const baseCameraKey = `entitytypes/${typeId}/entities/${id}`;
  return useSwr<ApiResponse<CameraEntity>>(
    `${baseCameraKey}?includeTriggerResult=true`,
    config
  );
};

export const getCamera = async (
  id: string,
  typeId: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<ApiResponse<CameraEntity>>> => {
  const baseCameraKey = `entitytypes/${typeId}/entities/${id}`;
  return axiosInst(`${baseCameraKey}?includeTriggerResult=true`, config);
};

export const useCameraPresets = (
  cameraId: string,
  skipLoad: boolean,
  config?: SWRConfiguration
): ServerResponse<ApiArrayResponse<HvsEntity>> => {
  const baseCameraKey = `systemtypes/preset/entities?pageNr=1&pageSize=9999&hostId=${cameraId}&sortField=source_id`;
  return useSwr<ApiArrayResponse<HvsEntity>>(
    skipLoad ? null : `${baseCameraKey}`,
    config
  );
};

export const getCameraPresets = (
  cameraId: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<ApiArrayResponse<HvsEntity>>> => {
  const baseCameraKey = `systemtypes/preset/entities?pageNr=1&pageSize=9999&hostId=${cameraId}&sortField=source_id`;
  return axiosInst.get(`${baseCameraKey}`, config);
};

export const updateCamera = (
  id: string,
  typeId: string,
  data: Partial<CameraEntity>,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
  const baseCameraKey = `entitytypes/${typeId}/entities/${id}`;
  return axiosInst.put(`${baseCameraKey}`, data, config);
};

export const addCamera = (
  typeId: string,
  data: Partial<CameraEntity>,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
  const baseCameraKey = `entitytypes/${typeId}/entities/`;
  return axiosInst.post(`${baseCameraKey}`, data, config);
};
